import { useState } from 'react'
import {
  App as AntdApp,
  Button,
  Card,
  Form,
  Grid,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CreditCardOutlined,
  PlusOutlined,
  DollarOutlined,
  CalendarOutlined,
  WalletOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

interface Payment {
  id: number
  customer_id: number
  invoice_id?: number
  amount: string
  currency: string
  received_at: string
  reference?: string
  source_kind: string
  source_ref?: string
  created_at: string
  customer_name?: string
  customer_no?: string
}

interface CustomerLite {
  id: number
  customer_no: string
  email?: string
  company?: string
}

function todayStr() {
  return new Date().toISOString().slice(0, 10)
}
function monthStartStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

export default function Payments() {
  const { t } = useTranslation()
  const [customerID, setCustomerID] = useState<number | null>(null)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sourceKind, setSourceKind] = useState('')
  const [openRecord, setOpenRecord] = useState(false)
  const [refundPayment, setRefundPayment] = useState<Payment | null>(null)
  const qc = useQueryClient()

  const customersQ = useQuery({
    queryKey: ['customers-for-payments'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: CustomerLite[] } }>(
        '/api/v1/customers?limit=500',
      )
      return res.data.data.items
    },
  })

  const qs = new URLSearchParams()
  if (customerID) qs.set('customer_id', String(customerID))
  if (dateFrom) qs.set('date_from', dateFrom)
  if (dateTo) qs.set('date_to', dateTo)
  if (sourceKind) qs.set('source_kind', sourceKind)
  qs.set('limit', '200')

  const listQ = useQuery({
    queryKey: ['payments', customerID, dateFrom, dateTo, sourceKind],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: Payment[]; total: number } }>(
        `/api/v1/payments?${qs}`,
      )
      return res.data.data
    },
  })

  const rows = listQ.data?.items ?? []
  const total = listQ.data?.total ?? 0

  const todayTotal = rows
    .filter(r => r.received_at.slice(0, 10) === todayStr() && parseFloat(r.amount) > 0)
    .reduce((s, r) => s + parseFloat(r.amount), 0)
  const monthTotal = rows
    .filter(r => r.received_at.slice(0, 10) >= monthStartStr() && parseFloat(r.amount) > 0)
    .reduce((s, r) => s + parseFloat(r.amount), 0)
  const refundTotal = rows
    .filter(r => parseFloat(r.amount) < 0)
    .reduce((s, r) => s + parseFloat(r.amount), 0)
  const currency = rows[0]?.currency ?? 'EUR'

  const columns: ColumnsType<Payment> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: t('payment.receivedAt', 'Eingang'), dataIndex: 'received_at', key: 'rec', width: 130,
      render: (v: string) => new Date(v).toLocaleDateString('de-DE') },
    { title: t('payment.customer', 'Kunde'), dataIndex: 'customer_id', key: 'cust',
      render: (v: number, r) => (
        <Link to={`/customers/${v}`}>{r.customer_name || r.customer_no || `#${v}`}</Link>
      ) },
    { title: t('payment.amount', 'Betrag'), key: 'amount', align: 'right' as const, width: 140,
      render: (_, r) => (
        <strong className={parseFloat(r.amount) < 0 ? 'text-danger' : ''}>
          {r.amount} {r.currency}
        </strong>
      ) },
    { title: t('payment.invoice', 'Rechnung'), dataIndex: 'invoice_id', key: 'inv', width: 110,
      render: (v?: number) => v ? <Link to={`/invoices/${v}`}>#{v}</Link> : '—' },
    { title: t('payment.source', 'Quelle'), dataIndex: 'source_kind', key: 'src', width: 120,
      render: (v: string) => <Tag>{v}</Tag> },
    { title: t('payment.reference', 'Referenz'), dataIndex: 'reference', key: 'ref',
      ellipsis: true },
    { title: '', key: 'actions', width: 110, fixed: 'right' as const, align: 'right' as const,
      render: (_, r) => parseFloat(r.amount) > 0 ? (
        <Button size="small" icon={<UndoOutlined />}
          onClick={() => setRefundPayment(r)}>
          {t('payment.refund', 'Refund')}
        </Button>
      ) : (
        <Tag color="red">{t('payment.refundRow', 'Refund')}</Tag>
      ),
    },
  ]

  const renderMobileCard = (r: Payment) => {
    const num = parseFloat(r.amount)
    const isRefund = num < 0
    return (
      <div className={`mob-card${isRefund ? ' mob-card--urgent' : ''}`}>
        <div className="flex-between mb-6">
          <Link to={`/customers/${r.customer_id}`} className="font-medium">
            {r.customer_name || r.customer_no || `#${r.customer_id}`}
          </Link>
          <Typography.Text strong style={{ color: isRefund ? '#DC2626' : '#059669' }}>
            {num >= 0 ? '+' : ''}{num.toFixed(2)} {r.currency}
          </Typography.Text>
        </div>
        <div className="flex-between">
          <Space size={4}>
            <Tag className="text-xs">{r.source_kind}</Tag>
            {r.invoice_id && (
              <Link to={`/invoices/${r.invoice_id}`} className="text-sm">
                Rechnung #{r.invoice_id}
              </Link>
            )}
          </Space>
          <Typography.Text type="secondary" className="text-sm">
            {new Date(r.received_at).toLocaleDateString('de-DE')}
          </Typography.Text>
        </div>
        {num > 0 && (
          <div className="mt-8">
            <Button size="small" icon={<UndoOutlined />} onClick={() => setRefundPayment(r)}>
              Refund
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      <PageHeader
        icon={<CreditCardOutlined />}
        title={t('payment.title', 'Zahlungen')}
        subtitle={t('payment.subtitle', 'Eingegangene Zahlungen, Refunds und Abstimmung mit offenen Rechnungen.')}
        extra={
          <Space wrap>
            <Button href="/api/v1/payments/export.csv">
              {t('payment.exportCsv', 'CSV-Export')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setOpenRecord(true)}
            >
              {t('payment.record', 'Zahlung erfassen')}
            </Button>
          </Space>
        }
      />

      <div className="kpi-grid kpi-grid-4 mb-16">
        <div className="kpi-tile kpi-success">
          <div className="kpi-tile-label"><DollarOutlined /> {t('payment.today', 'Heute')}</div>
          <div className="kpi-tile-value">{todayTotal.toFixed(2)} {currency}</div>
        </div>
        <div className="kpi-tile kpi-info">
          <div className="kpi-tile-label"><CalendarOutlined /> {t('payment.thisMonth', 'Diesen Monat')}</div>
          <div className="kpi-tile-value">{monthTotal.toFixed(2)} {currency}</div>
        </div>
        <div className="kpi-tile">
          <div className="kpi-tile-label"><WalletOutlined /> {t('payment.totalShown', 'Angezeigt')}</div>
          <div className="kpi-tile-value">{total}</div>
          <div className="kpi-tile-sub">{t('payment.transactions', 'Transaktionen')}</div>
        </div>
        <div className="kpi-tile kpi-danger">
          <div className="kpi-tile-label"><UndoOutlined /> {t('payment.refunds', 'Refunds')}</div>
          <div className="kpi-tile-value">{Math.abs(refundTotal).toFixed(2)} {currency}</div>
        </div>
      </div>

      <Card className="content-card">
        <Space wrap className="mb-16">
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder={t('payment.filterCustomer', 'Alle Kunden')}
            className="min-w-240"
            options={(customersQ.data || []).map(c => ({
              value: c.id,
              label: `${c.customer_no} — ${c.company || c.email || '?'}`,
            }))}
            onChange={(v: number | undefined) => setCustomerID(v ?? null)}
            value={customerID ?? undefined}
          />
          <Input
            placeholder={t('payment.dateFrom', 'Von (YYYY-MM-DD)')}
            className="w-160"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
          <Input
            placeholder={t('payment.dateTo', 'Bis (YYYY-MM-DD)')}
            className="w-160"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
          <Select
            allowClear
            placeholder={t('payment.filterSource', 'Alle Quellen')}
            className="w-160"
            value={sourceKind || undefined}
            onChange={(v: string | undefined) => setSourceKind(v ?? '')}
            options={[
              { value: 'banktransfer', label: t('payment.banktransfer', 'Überweisung') },
              { value: 'manual', label: t('payment.manual', 'Manuell') },
              { value: 'sepa', label: 'SEPA-Lastschrift' },
              { value: 'stripe', label: 'Stripe' },
              { value: 'mollie', label: 'Mollie' },
              { value: 'paypal', label: 'PayPal' },
              { value: 'camt53', label: t('payment.camt53', 'Überweisung (Kontoauszug-Import)') },
            ]}
          />
          <Button onClick={() => {
            setCustomerID(null); setDateFrom(''); setDateTo(''); setSourceKind('')
          }}>
            {t('common.reset', 'Zurücksetzen')}
          </Button>
        </Space>
        <DataTable<Payment>
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={listQ.isLoading}
          scroll={{ x: 900 }}
          renderMobileCard={renderMobileCard}
        />
      </Card>
      <RecordPaymentModal
        customers={customersQ.data || []}
        open={openRecord}
        onClose={() => {
          setOpenRecord(false)
          qc.invalidateQueries({ queryKey: ['payments'] })
        }}
      />
      <RefundModal payment={refundPayment} onClose={() => {
        setRefundPayment(null)
        qc.invalidateQueries({ queryKey: ['payments'] })
      }} />
    </>
  )
}

function RefundModal({ payment, onClose }: {
  payment: Payment | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()

  const refund = useMutation({
    mutationFn: async (vals: { amount?: string; reason?: string }) => {
      if (!payment) return
      await apiClient.post(`/api/v1/payments/${payment.id}/refund`, {
        amount: vals.amount || undefined,
        reason: vals.reason || undefined,
      })
    },
    onSuccess: () => {
      message.success(t('payment.refundDone', 'Refund verbucht'))
      qc.invalidateQueries({ queryKey: ['payments'] })
      form.resetFields()
      onClose()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error ||
        t('payment.refundFailed', 'Refund fehlgeschlagen'))
    },
  })

  return (
    <Modal
      open={!!payment}
      title={t('payment.refundTitle', 'Zahlung als Refund verbuchen')}
      onCancel={onClose}
      onOk={() => form.submit()}
      okButtonProps={{ loading: refund.isPending, danger: true }}
      okText={t('payment.refundConfirm', 'Refund verbuchen')}
      destroyOnClose
    >
      {payment && (
        <>
          <Typography.Paragraph type="secondary">
            {t('payment.refundIntro',
              'Erstellt eine negative Zahlung als Refund-Buchung. Die tatsächliche Rückerstattung erfolgt außerhalb des Systems (Stripe-Dashboard, manuelle Banküberweisung).')}
          </Typography.Paragraph>
          <Typography.Paragraph>
            <strong>{t('payment.original', 'Original')}:</strong>{' '}
            {payment.amount} {payment.currency} · {payment.source_kind}
            {' · '}
            {new Date(payment.received_at).toLocaleDateString('de-DE')}
          </Typography.Paragraph>
          <Form form={form} layout="vertical"
            initialValues={{ amount: payment.amount }}
            onFinish={(v) => refund.mutate(v as { amount?: string; reason?: string })}>
            <Form.Item name="amount"
              label={t('payment.refundAmount', 'Refund-Betrag')}
              extra={t('payment.refundAmountHint',
                'Max = ursprünglicher Betrag minus bereits erstattete. Leer = voller Refund.')}>
              <Input suffix={payment.currency} />
            </Form.Item>
            <Form.Item name="reason" label={t('payment.refundReason', 'Grund (optional)')}>
              <Input.TextArea rows={3} placeholder={
                t('payment.refundReasonPlaceholder',
                  'Stripe-Refund-ID, Kunden-Reklamation, …')} />
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  )
}

function RecordPaymentModal({
  customers,
  open,
  onClose,
}: {
  customers: CustomerLite[]
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const screens = Grid.useBreakpoint()
  const [form] = Form.useForm()

  const record = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      await apiClient.post('/api/v1/payments', values)
    },
    onSuccess: () => {
      message.success(t('payment.recorded', 'Zahlung erfasst'))
      form.resetFields()
      onClose()
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  const today = new Date().toISOString().slice(0, 10)

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('payment.record', 'Zahlung erfassen')}
      onOk={() => form.submit()}
      okButtonProps={{ loading: record.isPending }}
      width={screens.sm ? 480 : '95vw'}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ received_at: today, currency: 'EUR', source_kind: 'manual' }}
        onFinish={(v) => record.mutate(v)}
      >
        <Form.Item name="customer_id" label={t('payment.customer', 'Kunde')}
          rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('payment.pickCustomer', 'Kunde wählen')}
            options={customers.map(c => ({
              value: c.id,
              label: `${c.customer_no} — ${c.company || c.email || '?'}`,
            }))}
          />
        </Form.Item>
        <Form.Item name="amount" label={t('payment.amount', 'Betrag')} rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="currency" label={t('payment.currency', 'Währung')}
          rules={[{ required: true, len: 3 }]}>
          <Input maxLength={3} />
        </Form.Item>
        <Form.Item name="received_at" label={t('payment.receivedAt', 'Eingang')}
          rules={[{ required: true, pattern: /^\d{4}-\d{2}-\d{2}$/ }]}>
          <Input placeholder="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="invoice_id" label={t('payment.invoiceID', 'Rechnungs-ID (optional)')}>
          <Input type="number" />
        </Form.Item>
        <Form.Item name="reference" label={t('payment.reference', 'Referenz')}>
          <Input />
        </Form.Item>
        <Form.Item name="bank_account" label={t('payment.bankAccount', 'Bankkonto-IBAN')}>
          <Input />
        </Form.Item>
        <Form.Item name="source_kind" label={t('payment.source', 'Quelle')}>
          <Select options={[
            { value: 'banktransfer', label: t('payment.banktransfer', 'Überweisung') },
            { value: 'manual', label: t('payment.manual', 'Manuell') },
            { value: 'sepa', label: 'SEPA-Lastschrift' },
            { value: 'stripe', label: 'Stripe' },
            { value: 'paypal', label: 'PayPal' },
            { value: 'mollie', label: 'Mollie' },
          ]} />
        </Form.Item>
        <Form.Item name="notes" label={t('payment.notes', 'Notizen')}>
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
